/**
 * 取得統一的 cookie 選項
 *
 * 【重要】認證相關的 cookie 一律使用 host-only（不設定 domain）。
 *
 * 過去這裡會把 domain 設成根網域（例如 .momentx.ai），導致所有子網域共用同一份
 * Authorization / RefreshToken：在正式站登入拿到的 token 會被自動帶去 alpha 站送給
 * gateway-test，反之亦然。那顆 token 對另一邊的後端無效，於是每支 API 都 401、
 * refresh 也拿錯環境的 refresh token 去換而失敗，最後變成「一直被踢回登入頁」。
 *
 * 同時 localStorage 是 per-origin 的，不會跟著跨子網域走，所以使用者被彈到別的子網域時
 * 主題偏好（color-mode 的 `theme` key）也會一併「消失」而回到系統預設。
 *
 * 改成 host-only 之後，每個環境各自持有自己的登入狀態，互不汙染。
 *
 * @param options 額外的 cookie 選項（如 default, maxAge, sameSite 等）
 * @returns 統一的 cookie 選項，與 Nuxt 的 CookieOptions 類型兼容
 */
export const useCookieOptions = <T = string>(
  options: {
    default?: () => T;
    maxAge?: number;
    sameSite?: 'strict' | 'lax' | 'none';
    path?: string;
  } = {}
): {
  default?: () => T;
  maxAge?: number;
  sameSite?: 'strict' | 'lax' | 'none';
  path?: string;
} => {
  const result: {
    default?: () => T;
    maxAge?: number;
    sameSite?: 'strict' | 'lax' | 'none';
    path?: string;
  } = {};

  // 只在有提供 default 時才設定
  if (options.default) {
    result.default = options.default;
  }

  // 只在有提供時才設定這些選項
  if (options.maxAge !== undefined) {
    result.maxAge = options.maxAge;
  }
  if (options.sameSite !== undefined) {
    result.sameSite = options.sameSite;
  }
  // path 預設為 '/'，但如果明確設定了就使用設定的值
  result.path = options.path ?? '/';

  return result;
};

/**
 * 取得父網域（例如 app.momentx.ai → .momentx.ai）
 *
 * 前端自己寫的認證 cookie 一律 host-only（見上），但父網域上仍會出現同名的 cookie，
 * 有兩個來源，兩者都只能靠這個網域字串才刪得掉：
 *
 *   1. 舊版前端留下的殘留（改成 host-only 之前寫的）
 *   2. Google 登入時由後端（gateway 與前端不同 host）用 `Domain=.momentx.ai` 寫入的
 *      Authorization / RefreshToken / memberinfo——這些帶 HttpOnly，JS 完全刪不掉，
 *      必須由同源的 server route 回 Set-Cookie 清除，見 `server/routes/_auth/logout.post.ts`
 *
 * @param host 主機名稱（例如：app.momentx.ai）
 * @returns 父網域（例如：.momentx.ai）；localhost 或 IP 回傳空字串
 */
export const getParentCookieDomain = (host: string): string => {
  if (host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return '';
  }
  const parts = host.split('.');
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return '';
};

/**
 * 列出這個 host 上「JS 寫得出來」的所有 cookie domain scope
 *
 * 用途是把某個名稱的 domain-scoped cookie 一次刪乾淨，好讓 `document.cookie` 裡
 * 剩下的同名值必然是 host-only 那份 —— 因為 `document.cookie` 只給值、不給 scope，
 * 這是前端唯一能「證明」手上那份 cookie 屬於本 host 的辦法（見
 * `plugins/00.authCookieMigration.client.ts`）。
 *
 * 例：`a.b.momentx.ai` → `['.b.momentx.ai', '.momentx.ai']`
 *
 * 刻意**不含** host 自己那層（`.a.b.momentx.ai`）：依 RFC 6265 §5.3 cookie 的儲存鍵是
 * (name, domain, path)，帶 `Domain=<自身 host>` 的刪除在部分實作（含 jsdom/tough-cookie）
 * 會連 host-only 那份一起清掉——而 host-only 正是我們要保護的那份。
 * 本專案的舊版殘留一律寫在父網域上（見 `getParentCookieDomain`），不需要涵蓋自身那層。
 * 同理也不含 public suffix（`.ai`），瀏覽器本來就不接受。
 *
 * @param host 主機名稱（例如：app.momentx.ai）
 * @returns 由窄到寬的祖先 domain 屬性值；localhost、IP 或二段網域回傳空陣列
 */
export const getCookieDomainCandidates = (host: string): string[] => {
  if (host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return [];
  }
  const parts = host.split('.');

  const candidates: string[] = [];
  for (let i = 1; i <= parts.length - 2; i++) {
    candidates.push(`.${parts.slice(i).join('.')}`);
  }
  return candidates;
};
